import { useAuth } from '../../auth/useAuth';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Btn, GameBadge, Icon } from '../../components/chancey';
import { safeAreaPadding } from '../../components/layout/safeArea';
import { useGameApi } from '../../api/games';
import type { RulesStatsResponse } from '../../api/types';
import { SMART_PICK } from '../../data/lotteryData';
import { useActiveGame } from '../../hooks/useActiveGame';
import { useChanceyTheme } from '../../theme/ThemeContext';
import { THEMES } from '../../theme/tokens';
import { buildRulesStatsPayload } from '../../rules/buildRulesStatsPayload';
import { previewPickIsValidForGame } from '../../rules/previewPick';
import {
  ticketRulesForSimpleVibe,
  type SimpleVibeId,
} from '../../rules/simpleVibeStacks';
import { ruleBuilderService } from '../../services/ruleBuilderService';
import { track } from '../../services/analyticsService';
import { useRuleBuilderStore } from '../../stores/ruleBuilderStore';

type CombinedPool = RulesStatsResponse['combined'];
type StatsByVibe = Partial<Record<SimpleVibeId, RulesStatsResponse>>;

function formatPoolOdds(combined: CombinedPool): string {
  return `1 : ${combined.remainingPoolSize.toLocaleString()} (${combined.method})`;
}

const SIMPLE_VIBES: {
  id: SimpleVibeId;
  name: string;
  tag?: string;
  desc: string;
  icon: 'flame' | 'clock' | 'sparkle';
}[] = [
  {
    id: 'hot',
    name: 'Lucky & hot',
    tag: 'Most popular',
    desc: 'Picks lean toward numbers that have been showing up a lot lately.',
    icon: 'flame',
  },
  {
    id: 'overdue',
    name: 'Due to hit',
    desc: "Favors numbers that haven't appeared in a while — feeling overdue.",
    icon: 'clock',
  },
  {
    id: 'balanced',
    name: 'Balanced',
    tag: 'Recommended',
    desc: 'A safe mix — some hot numbers, some overdue, spread across ranges.',
    icon: 'sparkle',
  },
];

export interface RulesSimpleModeProps {
  onSwitchAdvanced: () => void;
}

export const RulesSimpleMode: React.FC<RulesSimpleModeProps> = ({ onSwitchAdvanced }) => {
  const { c, typ, d, dark, themeName } = useChanceyTheme();
  const { isSignedIn, isLoaded: authLoaded } = useAuth();
  const { postRulesStats } = useGameApi();
  const { game, gameId } = useActiveGame();
  const vibe = useRuleBuilderStore((s) => s.simpleVibe);

  const [generated, setGenerated] = useState(false);
  const [animKey, setAnimKey] = useState(0);
  const [simpleStats, setSimpleStats] = useState<RulesStatsResponse | null>(null);
  const [statsByVibe, setStatsByVibe] = useState<StatsByVibe>({});
  const [genBusy, setGenBusy] = useState(false);
  const [genError, setGenError] = useState<string | null>(null);

  const vibeRef = useRef(vibe);
  vibeRef.current = vibe;
  const statsByVibeRef = useRef<StatsByVibe>({});
  const postRulesStatsRef = useRef(postRulesStats);
  postRulesStatsRef.current = postRulesStats;
  const requestSeqRef = useRef(0);

  const postVibeStack = useCallback(async (targetVibe: SimpleVibeId) => {
    const stack = ticketRulesForSimpleVibe(targetVibe);
    return postRulesStatsRef.current(gameId, buildRulesStatsPayload(stack));
  }, [gameId]);

  const loadVibeStats = useCallback((targetVibe: SimpleVibeId, source: 'auto' | 'manual' = 'auto') => {
    if (!authLoaded || !isSignedIn) return;
    const requestSeq = ++requestSeqRef.current;
    setSimpleStats(null);
    setGenBusy(true);
    setGenError(null);

    void postVibeStack(targetVibe)
      .then((res) => {
        if (requestSeq !== requestSeqRef.current) return;
        track('smart_pick_generate', {
          gameId,
          source,
          outcome: targetVibe,
        });
        const nextStats = { ...statsByVibeRef.current, [targetVibe]: res };
        statsByVibeRef.current = nextStats;
        setStatsByVibe(nextStats);
        if (vibeRef.current === targetVibe) {
          setSimpleStats(res);
          setGenerated(true);
          setGenError(null);
        }
      })
      .catch((e) => {
        if (requestSeq !== requestSeqRef.current || vibeRef.current !== targetVibe) return;
        track('smart_pick_error', {
          gameId,
          source,
          outcome: targetVibe,
          errorCode: e instanceof Error ? e.name : 'unknown',
        });
        setGenError(e instanceof Error ? e.message : 'Could not generate pick');
      })
      .finally(() => {
        if (requestSeq === requestSeqRef.current && vibeRef.current === targetVibe) {
          setGenBusy(false);
        }
      });
  }, [authLoaded, gameId, isSignedIn, postVibeStack]);

  /** Per-game: load the active vibe only; other vibes lazy-load when selected. */
  useEffect(() => {
    if (!authLoaded || !isSignedIn) {
      requestSeqRef.current += 1;
      statsByVibeRef.current = {};
      setStatsByVibe({});
      setSimpleStats(null);
      setGenerated(false);
      setGenError(null);
      setGenBusy(false);
      return;
    }
    statsByVibeRef.current = {};
    setStatsByVibe({});
    setGenerated(false);

    loadVibeStats(vibe, 'auto');
  }, [gameId, authLoaded, isSignedIn, loadVibeStats, vibe]);

  const currentVibe = SIMPLE_VIBES.find((v) => v.id === vibe) ?? SIMPLE_VIBES[2];

  const previewLoading = Boolean(authLoaded && isSignedIn && genBusy);

  const displayPick = useMemo((): { main: number[]; bonus: number[] } => {
    const g = simpleStats?.game;
    const pick = simpleStats?.previewPick;
    if (g && pick && previewPickIsValidForGame(pick, g)) return pick;
    return { main: [...SMART_PICK.main], bonus: [...SMART_PICK.bonus] };
  }, [simpleStats]);

  const previewAnimSignature = useMemo(
    () =>
      simpleStats
        ? `${JSON.stringify(simpleStats.previewPick ?? [])}:${simpleStats.combined.remainingPoolSize}`
        : '',
    [simpleStats]
  );

  const previewAnimPrev = useRef<string | null>(null);
  const pickShapeRef = useRef({
    main: SMART_PICK.main.length,
    bonus: SMART_PICK.bonus.length,
  });
  useEffect(() => {
    const g = simpleStats?.game;
    if (g) {
      pickShapeRef.current = { main: g.mainCount, bonus: g.bonusCount };
    }
  }, [simpleStats?.game]);

  useEffect(() => {
    if (!previewAnimSignature) return;
    if (previewAnimPrev.current === null) {
      previewAnimPrev.current = previewAnimSignature;
      return;
    }
    if (previewAnimPrev.current !== previewAnimSignature) {
      previewAnimPrev.current = previewAnimSignature;
      setAnimKey((k) => k + 1);
    }
  }, [previewAnimSignature]);

  useEffect(() => {
    const cached = statsByVibe[vibe];
    if (!cached || simpleStats === cached) return;
    setSimpleStats(cached);
    setGenerated(true);
    setGenError(null);
    setGenBusy(false);
  }, [simpleStats, statsByVibe, vibe]);

  const selectVibe = useCallback(
    (nextVibe: SimpleVibeId) => {
      ruleBuilderService.setSimpleVibe(nextVibe);
      vibeRef.current = nextVibe;
      setGenError(null);
    },
    []
  );

  /** Same vibe again: ask the server for another sample pick (MC path may differ). */
  const runGenerate = useCallback(() => {
    if (!authLoaded || !isSignedIn) return;
    loadVibeStats(vibe, 'manual');
  }, [authLoaded, isSignedIn, vibe, loadVibeStats]);

  return (
    <>
      <style>{`
          @keyframes revealPop {
            0% { transform: scale(0.4); opacity: 0; }
            60% { transform: scale(1.1); opacity: 1; }
            100% { transform: scale(1); opacity: 1; }
          }
          @keyframes pickSkeletonPulse {
            0%, 100% { opacity: 0.35; }
            50% { opacity: 0.7; }
          }
          @keyframes pickBarSweep {
            0% { transform: translateX(-120%); }
            100% { transform: translateX(350%); }
          }
        `}</style>
      <div
        className="relative flex flex-col"
        style={{ ...safeAreaPadding(8, d.pad, 16, d.pad), gap: d.gap, color: c.ink }}
      >
        <div className="flex flex-col items-start gap-1.5 px-0.5 pb-0.5 pt-1">
          <GameBadge game={game} />
          <div
            className="text-[32px] leading-tight"
            style={{
              fontFamily: typ.display,
              fontWeight: typ.displayWeight,
              letterSpacing: typ.displayLetter,
            }}
          >
            Smart pick
          </div>
          <div className="mt-1.5 max-w-[320px] text-[13px] leading-snug" style={{ color: c.inkSoft, fontFamily: typ.body }}>
            Pick a vibe — we&apos;ll handle the math.
          </div>
        </div>

        <div
          className="relative overflow-hidden rounded-[24px] p-[18px] text-[color:var(--chancey-ink)]"
          style={{
            background: dark ? THEMES[themeName].light.ink : c.ink,
            color: dark ? c.ink : c.bg,
          }}
        >
          <div className="absolute -right-3 -top-3 h-[60px] w-[60px] rounded-full opacity-25" style={{ background: c.accent }} />
          <div className="absolute -bottom-5 -left-5 h-20 w-20 rounded-full opacity-[0.12]" style={{ background: c.accent2 }} />
          <div className="relative mb-3.5 flex items-center justify-between">
            <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-white/70" style={{ fontFamily: typ.body }}>
              {previewLoading ? 'Generating pick' : generated ? 'Your pick' : 'Preview pick'}
            </div>
            <div className="font-mono text-[11px] font-semibold" style={{ color: 'currentColor', opacity: 0.85, fontFamily: typ.mono }}>
              {(() => {
                const pool =
                  simpleStats && !genError
                    ? simpleStats.combined
                    : statsByVibe[vibe] && !genError
                      ? statsByVibe[vibe]?.combined
                      : null;
                return pool ? <>Pool {formatPoolOdds(pool)}</> : <>{currentVibe.name}</>;
              })()}
            </div>
          </div>
          {previewLoading ? (
            <div className="relative">
              <div className="flex min-h-[44px] flex-wrap items-center gap-2">
                {Array.from({
                  length: pickShapeRef.current.main + pickShapeRef.current.bonus,
                }).map((_, i) => (
                  <div
                    key={i}
                    className="shrink-0 rounded-full bg-white/25"
                    style={{
                      width: 44,
                      height: 44,
                      animation: `pickSkeletonPulse 1s ease-in-out ${i * 0.08}s infinite`,
                    }}
                  />
                ))}
              </div>
              <div
                className="mt-3 h-[3px] w-full overflow-hidden rounded-full bg-white/15"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label="Generating pick"
              >
                <div
                  className="h-full w-[38%] rounded-full bg-white/55"
                  style={{
                    animation: 'pickBarSweep 1.15s ease-in-out infinite',
                  }}
                />
              </div>
              <p className="mt-2 text-[12px] leading-snug text-white/65" style={{ fontFamily: typ.body }}>
                Shaping numbers for {currentVibe.name}…
              </p>
            </div>
          ) : (
            <div
              key={animKey}
              className="relative flex items-center"
              style={{ gap: 'clamp(3px, 1.7vw, 8px)' }}
            >
              {displayPick.main.map((n, i) => (
                <div
                  key={`${n}-${i}`}
                  className="flex shrink-0 items-center justify-center rounded-full font-mono text-[17px] font-bold shadow-lg"
                  style={{
                    width: 'clamp(32px, 10vw, 44px)',
                    height: 'clamp(32px, 10vw, 44px)',
                    borderRadius: 999,
                    background: c.bg,
                    color: c.ink,
                    fontFamily: typ.mono,
                    fontSize: 'clamp(13px, 4vw, 17px)',
                    animation: `revealPop 0.4s ${i * 0.06}s cubic-bezier(.2,1.4,.3,1) both`,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
                  }}
                >
                  {n}
                </div>
              ))}
              <div className="mx-0.5 h-7 w-px shrink-0 bg-white/20" style={{ width: 1, height: 28 }} />
              {displayPick.bonus.map((n, i) => (
                <div
                  key={`bonus-${n}-${i}`}
                  className="flex shrink-0 items-center justify-center rounded-full font-mono text-[17px] font-bold text-white shadow-lg"
                  style={{
                    width: 'clamp(32px, 10vw, 44px)',
                    height: 'clamp(32px, 10vw, 44px)',
                    borderRadius: 999,
                    background: c.lucky,
                    fontFamily: typ.mono,
                    fontSize: 'clamp(13px, 4vw, 17px)',
                    animation: `revealPop 0.4s ${Math.min(displayPick.main.length + i, 5) * 0.06}s cubic-bezier(.2,1.4,.3,1) both`,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                  }}
                >
                  {n}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-1">
          <Btn
            c={c}
            typ={typ}
            variant="lucky"
            style={{ width: '100%' }}
            disabled={genBusy || !authLoaded || !isSignedIn}
            onClick={() => void runGenerate()}
            icon={Icon.wand(18, '#fff')}
          >
            {genBusy ? 'Generating…' : 'Generate pick'}
          </Btn>
        </div>

        <div
          className="px-0.5 pb-0 pt-1 text-[12px] font-medium uppercase tracking-[0.08em]"
          style={{ color: c.inkMuted, fontFamily: typ.body }}
        >
          Choose a vibe
        </div>
        <div className="flex flex-col gap-2">
          {SIMPLE_VIBES.map((v) => {
            const active = vibe === v.id;
            const VI = Icon[v.icon];
            const pool = statsByVibe[v.id]?.combined;
            const oddsLabel =
              !authLoaded || !isSignedIn
                ? 'Varies by game rules'
                : pool
                  ? formatPoolOdds(pool)
                  : genBusy
                    ? '…'
                    : '—';
            return (
              <button
                key={v.id}
                type="button"
                className="flex cursor-pointer items-center gap-3 rounded-2xl border-[1.5px] p-3.5 text-left transition-colors"
                style={{
                  borderColor: active ? c.accent : c.line,
                  background: c.surface,
                }}
                onClick={() => selectVibe(v.id)}
              >
                <div
                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl"
                  style={{
                    background: active ? c.gradient : c.surfaceAlt,
                  }}
                >
                  {VI(22, active ? '#fff' : c.ink)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="mb-0.5 flex flex-wrap items-center gap-1.5">
                    <span
                      className="text-[17px] leading-tight"
                      style={{
                        fontFamily: typ.display,
                        fontWeight: typ.displayWeight,
                        letterSpacing: typ.displayLetter,
                        color: c.ink,
                      }}
                    >
                      {v.name}
                    </span>
                    {v.tag ? (
                      <span
                        className="rounded-md px-1.5 py-px text-[9px] font-bold uppercase tracking-[0.06em]"
                        style={{
                          background: v.tag === 'Recommended' ? c.accent : c.surfaceAlt,
                          color: v.tag === 'Recommended' ? c.accentInk : c.inkSoft,
                          fontFamily: typ.body,
                        }}
                      >
                        {v.tag}
                      </span>
                    ) : null}
                  </div>
                  <p className="text-[12px] leading-snug" style={{ color: c.inkSoft, fontFamily: typ.body }}>
                    {v.desc}
                  </p>
                  <div className="mt-1.5 flex items-center gap-1.5">
                    <span
                      className="text-[10px] font-semibold uppercase tracking-[0.06em]"
                      style={{ color: c.inkMuted, fontFamily: typ.body }}
                    >
                      Odds
                    </span>
                    <span
                      className="font-mono text-[12px] font-bold"
                      style={{ color: active ? c.accent : c.ink, fontFamily: typ.mono }}
                    >
                      {oddsLabel}
                    </span>
                  </div>
                </div>
                <span
                  className="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full border-2"
                  style={{
                    borderColor: active ? c.accent : c.line,
                    background: active ? c.accent : 'transparent',
                  }}
                  aria-hidden
                >
                  {active ? <span className="block h-2 w-2 rounded-full bg-white" /> : null}
                </span>
              </button>
            );
          })}
        </div>

        {genError ? (
          <div className="text-center text-[12px]" style={{ color: c.lucky, fontFamily: typ.body }}>
            {genError}
          </div>
        ) : null}

        {!isSignedIn && authLoaded ? (
          <div className="text-center text-[12px]" style={{ color: c.inkMuted, fontFamily: typ.body }}>
            Sign in to load a pick for each vibe.
          </div>
        ) : null}

        <button
          type="button"
          className="py-3.5 text-center text-[12px] font-medium"
          style={{ color: c.inkSoft, fontFamily: typ.body }}
          onClick={onSwitchAdvanced}
        >
          Want fine control? <span style={{ color: c.accent, fontWeight: 600 }}>Switch to advanced →</span>
        </button>

        <div className="px-5 text-center text-[11px] leading-snug" style={{ color: c.inkMuted, fontFamily: typ.body }}>
          Lottery outcomes are random. Vibes shape which numbers get picked — they don&apos;t improve odds.
        </div>
      </div>
    </>
  );
};
